import { Directive, HostListener, Input, forwardRef, ElementRef, Renderer2 } from '@angular/core';
import { CountryCode } from 'libphonenumber-js';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PhoneNumberService, PhoneValidationResult } from '../services/phone-number.service';

@Directive({
  selector: '[appPhoneNumberInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputDirective),
      multi: true
    }
  ]
})
export class PhoneNumberInputDirective implements ControlValueAccessor {
  @Input('appPhoneNumberInput') defaultCountry?: CountryCode;
  @Input() emitE164 = true;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private lastRaw: string | null = null;

  constructor(
    private phoneNumberService: PhoneNumberService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  writeValue(value: string | null): void {
    this.lastRaw = value;
    if (value && this.defaultCountry) {
      const parseResult = this.phoneNumberService.parseNumber(value, this.defaultCountry);
      const formatted = parseResult.national || value;
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formatted);
    } else if (value) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', value);
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handled by Angular forms automatically on the host element.
  }

  @HostListener('input', ['$event'])
  onInput(event: any) {
    const value = event.target.value;
    this.lastRaw = value;

    // Format as user types
    const formatted = this.phoneNumberService.formatForInput(value, this.defaultCountry);

    // Update the visual display
    if (formatted !== value) {
      const cursorPosition = event.target.selectionStart;
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formatted);

      // Try to maintain cursor position
      const lengthDiff = formatted.length - value.length;
      this.renderer.setProperty(
        this.elementRef.nativeElement,
        'selectionStart',
        cursorPosition + lengthDiff
      );
      this.renderer.setProperty(
        this.elementRef.nativeElement,
        'selectionEnd',
        cursorPosition + lengthDiff
      );
    }

    // Parse and emit value
    const parseResult = this.phoneNumberService.parseNumber(formatted, this.defaultCountry);

    if (this.emitE164 && parseResult.e164) {
      this.onChange(parseResult.e164);
    } else {
      this.onChange(parseResult.national ?? formatted);
    }
  }

  @HostListener('blur')
  onBlur() {
    this.onTouched();
    if (!this.lastRaw) {
      this.onChange(null);
      return;
    }

    const parseResult: PhoneValidationResult = this.phoneNumberService.parseNumber(this.lastRaw, this.defaultCountry);
    this.onChange(this.emitE164 ? parseResult.e164 ?? this.lastRaw : parseResult.national ?? this.lastRaw);
  }
}
